import React from 'react'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import PageWidthContainerElement from '../../../designSystem/helpers/PageWidthContainer'
import {mq} from '../../../../utils/breakpoints'
import Card from '../../general/Card'
import {TierProgression} from './TierProgression'
import {TravelWithPlus} from './TravelWithPlus'
import {ExploreButton} from './ExploreButton'
import {PropsType} from './types'
import {MOBILE_HEADER_HEIGHT} from '../../general/CompactHeader/constants'

const SIDEBAR_MAX_WIDTH = 294
const CARD_MIN_HEIGHT = 360

const CardFullHeight = styled(Card)`
  overflow: auto;
  min-height: ${CARD_MIN_HEIGHT}px;
  position: relative;
  padding: 40px;
`

interface ContentWrapperProps {
  isMobile: boolean
  children: React.ReactNode
}

const SpacedContentWrapper = styled('div')(
  ({theme}) => css`
    padding: 0 ${theme.layout.spacing.s500}px;
  `
)

const PageWidthContainer = styled(PageWidthContainerElement)(
  ({theme}) =>
    css`
      height: 100%;
      ${mq.mobileSm(`
        padding: 0 ${theme.layout.spacing.s500}px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      `)}
    `
)

const PageContainerWrapper = styled('div')(
  ({theme}) =>
    css`
      height: calc(100vh - ${MOBILE_HEADER_HEIGHT}px);

      ${mq.desktopXs(css`
        height: auto;
        padding: ${theme.layout.spacing.s500}px 0;
      `)}
    `
)

const PageGrid = styled('div')(
  ({theme}) => css`
    margin-bottom: ${theme.layout.spacing.s600}px;
    padding-top: ${theme.layout.spacing.s200}px;
    margin-top: -3px;

    ${mq.desktopXs(css`
      display: grid;
      grid-template-columns:
        ${SIDEBAR_MAX_WIDTH}px
        minmax(min-content, auto);
      gap: ${theme.layout.spacing.s600}px;
      margin-bottom: 0;
      margin-top: 0;
    `)}
  `
)

const SideBarWrapper = styled(Card)`
  height: fit-content;
  overflow: auto;
`

const ContentWrapper = ({isMobile, children}: ContentWrapperProps) =>
  isMobile ? (
    <SpacedContentWrapper>{children}</SpacedContentWrapper>
  ) : (
    <CardFullHeight>{children}</CardFullHeight>
  )

const MobileFooter = styled.div<{isOverlay: boolean}>(({theme, isOverlay}) => {
  if (isOverlay) {
    return css`
      position: sticky;
      bottom: 0;
      padding: ${theme.layout.spacing.s500}px;
      box-shadow: ${theme.shadows.fixedReverse};
      background: ${theme.colors.defaultBg};
      margin-left: -${theme.layout.spacing.s500}px;
      margin-right: -${theme.layout.spacing.s500}px;
    `
  }

  return css`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: ${theme.layout.spacing.s500}px;
    box-shadow: ${theme.shadows.fixedReverse};
    background: ${theme.colors.defaultBg};
  `
})

const DesktopButtonWrapper = styled.div(
  ({theme}) =>
    css`
      width: 330px;
      margin: ${theme.layout.spacing.s800}px auto ${theme.layout.spacing.s700}px
        auto;
    `
)

const MembershipPageContent = ({
  isMobile,
  isAuthenticated,
  isMemberPlus,
  isLoading,
  logIn,
  anonymousId,
  profilePicture,
  accountSideMenu,
  isOverlay
}: PropsType) => {
  return (
    <PageContainerWrapper>
      <PageWidthContainer>
        <PageGrid>
          {!isMobile && (
            <SideBarWrapper>
              {isAuthenticated && profilePicture}
              {accountSideMenu}
            </SideBarWrapper>
          )}

          <div>
            {isAuthenticated && <TierProgression isMemberPlus={isMemberPlus} />}
            <ContentWrapper isMobile={isMobile}>
              <TravelWithPlus
                isMemberPlus={isMemberPlus}
                isOverlay={isOverlay}
              />
              {!isMobile && (
                <DesktopButtonWrapper>
                  {!isLoading && (
                    <ExploreButton
                      isAuthenticated={isAuthenticated}
                      isMemberPlus={isMemberPlus}
                      logIn={logIn}
                      anonymousId={anonymousId}
                    />
                  )}
                </DesktopButtonWrapper>
              )}
            </ContentWrapper>
          </div>
        </PageGrid>

        {isMobile && (
          <MobileFooter isOverlay={isOverlay}>
            {!isLoading && (
              <ExploreButton
                isAuthenticated={isAuthenticated}
                isMemberPlus={isMemberPlus}
                logIn={logIn}
                anonymousId={anonymousId}
              />
            )}
          </MobileFooter>
        )}
      </PageWidthContainer>
    </PageContainerWrapper>
  )
}

export default MembershipPageContent
